// API Client Setup (api.ts)
import axios from 'axios';
import { getAccessToken, setAccessToken, clearTokens } from './token-storage'; // Import new functions

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  // Keep withCredentials: true, as this is needed for the secure Refresh Token cookie
  withCredentials: true, 
});

// --- REQUEST INTERCEPTOR (Attaches Access Token from Memory) ---
api.interceptors.request.use((config) => {
  // 1. Get token from secure in-memory storage
  const accessToken = getAccessToken(); 
  
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

// --- RESPONSE INTERCEPTOR (Token Refresh and Retry) ---
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // Check for 401 and ensure it's not a retry
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        // 2. Refresh tokens using the secure HTTP-only Refresh Token cookie
        const refreshResponse = await axios.post(
          `${process.env.REACT_APP_API_URL}/auth/token/refresh/`, 
          {},
          { withCredentials: true }
        );
        
        // --- CRITICAL STEP ---
        // The backend must now return the *new* access token in the response body.
        // It should NOT set it as a JS-accessible cookie.
        const newAccessToken = refreshResponse.data?.access_token;
        if (newAccessToken) {
            // 3. Save the new Access Token to memory
            setAccessToken(newAccessToken);
            
            // 4. Update the original request header with the new token *before* retrying
            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
            
            // 5. Retry original request using the updated config
            return api(originalRequest);
        } else {
             // If refresh was successful but no new token was returned, something is wrong
             throw new Error('Refresh successful, but no new access token received.');
        }

      } catch (refreshError) {
        // Handle refresh failure (e.g., refresh token is expired)
        clearTokens(); // Clear memory token
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }
    
    return Promise.reject(error);
  }
);


export default api;